import React, { useEffect, useState } from "react";
import axios from "../api/Axios";

const StudentDocsTable = () => {
  const [students, setStudents] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxDocId, setLightboxDocId] = useState(null);
  const [lightboxDocType, setLightboxDocType] = useState('image'); // 'image' or 'pdf'
  const [expiryDate, setExpiryDate] = useState('');
  const [comments, setComments] = useState('');
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [academicRecords, setAcademicRecords] = useState([]);
  const [loadingAcademicRecords, setLoadingAcademicRecords] = useState(false);
  
  // Backend base URL for media files
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  // Helper function to determine file type
  const getFileType = (url) => {
    if (!url) return 'document';
    
    const extension = url.split('.').pop().toLowerCase();
    
    // PDF files
    if (['pdf'].includes(extension)) return 'pdf';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
    
    // Other document types (Word, Excel, PowerPoint, etc.)
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) return 'document';
    
    // Default to document for unknown types
    return 'document';
  };

  useEffect(() => {
    axios.get("/accounts/students-with-docs/")
      .then(res => setStudents(res.data))
      .catch(() => setStudents([]));
  }, []);

  // Handle document actions
  const handleAccept = async () => {
    if (!expiryDate) {
      alert("Please select an expiry date before accepting.");
      return;
    }
    
    try {
      // Update status to accepted
      await axios.post("/accounts/documents/update-status/", {
        document_id: lightboxDocId,
        status: "Accepted"
      });
      
      // Update expiry date
      await axios.post("/accounts/documents/update-expiry/", {
        document_id: lightboxDocId,
        expiry_date: expiryDate
      });
      
      alert("Document accepted successfully!");
      
      // Close lightbox and reset form
      setLightboxImg(null);
      setLightboxDocId(null);
      setLightboxDocType('image');
      setExpiryDate('');
      setComments('');
      
      // Refresh the students list
      const res = await axios.get("/accounts/students-with-docs/");
      setStudents(res.data);
    } catch (error) {
      console.error("Error accepting document:", error);
      alert("Error accepting document. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      alert("Please enter a comment before rejecting the document.");
      return;
    }
    
    try {
      // Update status to rejected
      await axios.post("/accounts/documents/update-status/", {
        document_id: lightboxDocId,
        status: "Rejected"
      });
      
      // Update comments
      await axios.post("/accounts/documents/update-comments/", {
        document_id: lightboxDocId,
        comments: comments
      });
      
      alert("Document rejected successfully!");
      
      // Close lightbox and reset form
      setLightboxImg(null);
      setLightboxDocId(null);
      setLightboxDocType('image');
      setExpiryDate('');
      setComments('');
      
      // Refresh the students list
      const res = await axios.get("/accounts/students-with-docs/");
      setStudents(res.data);
    } catch (error) {
      console.error("Error rejecting document:", error);
      alert("Error rejecting document. Please try again.");
    }
  };

  // Function to fetch academic records for a specific user
  const fetchAcademicRecords = async (userId) => {
    setLoadingAcademicRecords(true);
    try {
      const response = await axios.get(`/accounts/academic-records/user/${userId}/`);
      setAcademicRecords(response.data);
    } catch (error) {
      console.error('Error fetching academic records:', error);
      setAcademicRecords([]);
    } finally {
      setLoadingAcademicRecords(false);
    }
  };

  // Function to handle student details display
  const handleStudentDetails = async (student) => {
    setSelectedStudent(student);
    await fetchAcademicRecords(student.user?.id);
    setShowStudentDetails(true);
  };

  // Function to close student details lightbox
  const closeStudentDetails = () => {
    setShowStudentDetails(false);
    setSelectedStudent(null);
    setAcademicRecords([]);
    setLoadingAcademicRecords(false);
  };

  // Function to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="container my-4">
    
      
    

      
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Student Details</th>
              <th>Title</th>
              <th>Surname</th>
              <th>Other Names</th>
              <th>ID Type</th>
              <th>Institution</th>
              <th>Campus</th>
              <th>Documents</th>
            </tr>
          </thead>
          <tbody>
            {students.map(student => (
              <tr key={student.id}>
                <td>{student.id}</td>
                <td>
                  <button
                    onClick={() => handleStudentDetails(student)}
                    className="btn btn-info btn-sm"
                    style={{
                      backgroundColor: '#17a2b8',
                      border: 'none',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#138496'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
                  >
                    <i className="bi bi-person-circle"></i>
                    Student Details
                  </button>
                </td>
                <td>{student.title || 'N/A'}</td>
                <td>{student.surname}</td>
                <td>{student.other_names}</td>
                <td>{student.id_document_type || 'N/A'}</td>
                <td>{student.institution_name || 'N/A'}</td>
                <td>{student.campus?.name || 'N/A'}</td>
                <td>
                  {student.documents && student.documents.length > 0 ? (
                    student.documents.map(doc => {
                      // Handle URL construction properly - doc.url already starts with /media/
                      const documentUrl = doc.url.startsWith('/') ? 
                        `${BACKEND_URL}${doc.url}` : 
                        `${BACKEND_URL}/${doc.url}`;
                      
                      const fileType = getFileType(doc.url);
                      
                      return (
                        <button
                          key={doc.id}
                          onClick={() => {
                            setLightboxImg(documentUrl);
                            setLightboxDocId(doc.id);
                            setLightboxDocType(fileType);
                          }}
                          style={{ 
                            marginRight: 8, 
                            background: 'none', 
                            border: 'none', 
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {fileType === 'pdf' ? (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#e74c3c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              PDF
                            </div>
                          ) : fileType === 'document' ? (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#3498db',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                color: 'white',
                                fontSize: '8px',
                                fontWeight: 'bold'
                              }}
                            >
                              DOC
                            </div>
                          ) : (
                            <img
                              src={documentUrl}
                              alt="doc"
                              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
                              onError={(e) => {
                                // Fallback to DOC icon if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          )}
                          {fileType === 'image' && (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#95a5a6',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                color: 'white',
                                fontSize: '8px',
                                fontWeight: 'bold'
                              }}
                            >
                              DOC
                            </div>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-muted">No uploads</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lightboxImg && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }}
          onClick={(e) => {
            // Only close if clicking the backdrop, not the content
            if (e.target === e.currentTarget) {
              setLightboxImg(null);
              setLightboxDocId(null);
              setLightboxDocType('image');
              setExpiryDate('');
              setComments('');
            }
          }}
        >
          {/* Document viewer - conditional rendering based on file type */}
          {lightboxDocType === 'pdf' ? (
            <div
              style={{
                width: "80vw",
                height: "70vh",
                boxShadow: "0 0 30px #000",
                borderRadius: 8,
                background: "#fff",
                marginBottom: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px"
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: "#e74c3c",
                  marginBottom: "20px"
                }}
              >
                📄
              </div>
              <h3 style={{ color: "#333", marginBottom: "10px" }}>PDF Document</h3>
              <p style={{ color: "#666", textAlign: "center", marginBottom: "20px" }}>
                Click the button below to open the PDF document in a new tab.
              </p>
              <a
                href={lightboxImg}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "#e74c3c",
                  color: "white",
                  padding: "15px 30px",
                  textDecoration: "none",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                📖 Open PDF in New Tab
              </a>
            </div>
          ) : lightboxDocType === 'document' ? (
            <div
              style={{
                width: "80vw",
                height: "70vh",
                boxShadow: "0 0 30px #000",
                borderRadius: 8,
                background: "#fff",
                marginBottom: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px"
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: "#3498db",
                  marginBottom: "20px"
                }}
              >
                📄
              </div>
              <h3 style={{ color: "#333", marginBottom: "10px" }}>Document Viewer</h3>
              <p style={{ color: "#666", textAlign: "center", marginBottom: "20px" }}>
                This document format cannot be previewed directly in the browser.
              </p>
              <a
                href={lightboxImg}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "#3498db",
                  color: "white",
                  padding: "10px 20px",
                  textDecoration: "none",
                  borderRadius: "4px",
                  fontWeight: "bold"
                }}
              >
                Download & View Document
              </a>
            </div>
          ) : (
            <img
              src={lightboxImg}
              alt="Full Document"
              style={{
                maxWidth: "70vw",
                maxHeight: "60vh",
                boxShadow: "0 0 30px #000",
                borderRadius: 8,
                background: "#fff",
                marginBottom: "20px"
              }}
              onError={(e) => {
                console.error("Image failed to load:", e);
                e.target.style.display = 'none';
              }}
            />
          )}
          
          {/* Action panel */}
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 0 30px #000",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "15px",
              minWidth: "300px"
            }}
          >
            <h5 style={{ margin: "0 0 10px 0", color: "#333" }}>
              Document Actions (ID: {lightboxDocId})
            </h5>
            
            {/* Expiry Date input */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <label htmlFor="expiryDate" style={{ color: "#333", fontWeight: "bold" }}>
                Expiry Date:
              </label>
              <input
                id="expiryDate"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px"
                }}
              />
            </div>
            
            {/* Comments input */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <label htmlFor="comments" style={{ color: "#333", fontWeight: "bold" }}>
                Comments (required for rejection):
              </label>
              <textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Enter comments here..."
                rows={3}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  width: "250px",
                  resize: "vertical"
                }}
              />
            </div>
            
            {/* Action buttons */}
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
              <button
                onClick={handleAccept}
                style={{
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#218838"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#28a745"}
              >
                Accept
              </button>
              
              <button
                onClick={handleReject}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#c82333"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#dc3545"}
              >
                Reject
              </button>
            </div>
            
            {/* Close button */}
            <button
              onClick={() => {
                setLightboxImg(null);
                setLightboxDocId(null);
                setLightboxDocType('image');
                setExpiryDate('');
                setComments('');
              }}
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                marginTop: "10px"
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Student details lightbox */}
      {showStudentDetails && selectedStudent && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeStudentDetails();
            }
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "30px",
              borderRadius: "12px",
              boxShadow: "0 0 30px rgba(0,0,0,0.5)",
              maxWidth: "80vw",
              maxHeight: "80vh",
              overflowY: "auto",
              minWidth: "600px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, color: "#333", fontSize: "24px" }}>Student Details</h2>
              <button
                onClick={closeStudentDetails}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                ×
              </button>
            </div>

            {/* Student Personal Information */}
            <div style={{ marginBottom: "25px" }}>
              <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #007bff", paddingBottom: "5px" }}>
                Personal Information
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div>
                  <strong>ID:</strong> {selectedStudent.id}
                </div>
                <div>
                  <strong>Email:</strong> {selectedStudent.user?.email}
                </div>
                <div>
                  <strong>Title:</strong> {selectedStudent.title || 'N/A'}
                </div>
                <div>
                  <strong>Surname:</strong> {selectedStudent.surname}
                </div>
                <div>
                  <strong>Other Names:</strong> {selectedStudent.other_names}
                </div>
                <div>
                  <strong>Gender:</strong> {selectedStudent.gender || 'N/A'}
                </div>
                <div>
                  <strong>ID Type:</strong> {selectedStudent.id_document_type || 'N/A'}
                </div>
                <div>
                  <strong>ID Number:</strong> {selectedStudent.id_number || 'N/A'}
                </div>
                <div>
                  <strong>Phone:</strong> {selectedStudent.phone_number || 'N/A'}
                </div>
                <div>
                  <strong>Citizenship:</strong> {selectedStudent.citizenship || 'N/A'}
                </div>
                <div>
                  <strong>Student Number:</strong> {selectedStudent.student_number || 'N/A'}
                </div>
                <div>
                  <strong>Year of Study:</strong> {selectedStudent.year_of_study || 'N/A'}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div style={{ marginBottom: "25px" }}>
              <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #28a745", paddingBottom: "5px" }}>
                Address Information
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Address Lines:</strong> 
                  <div style={{ 
                    marginTop: "5px", 
                    padding: "8px", 
                    backgroundColor: "#f8f9fa", 
                    borderRadius: "4px", 
                    border: "1px solid #dee2e6",
                    whiteSpace: "pre-wrap" 
                  }}>
                    {selectedStudent.addresslines || 'N/A'}
                  </div>
                </div>
                <div>
                  <strong>Suburb:</strong> {selectedStudent.suburb || 'N/A'}
                </div>
                <div>
                  <strong>City:</strong> {selectedStudent.city || 'N/A'}
                </div>
                <div>
                  <strong>Province:</strong> {selectedStudent.province || 'N/A'}
                </div>
                <div>
                  <strong>Post Code:</strong> {selectedStudent.postcode || 'N/A'}
                </div>
                <div>
                  <strong>Country:</strong> {selectedStudent.country || 'N/A'}
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div style={{ marginBottom: "25px" }}>
              <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #ffc107", paddingBottom: "5px" }}>
                Academic Information
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div>
                  <strong>Institution:</strong> {selectedStudent.institution_name || 'N/A'}
                </div>
                <div>
                  <strong>Campus:</strong> {selectedStudent.campus?.name || 'N/A'}
                </div>
                <div>
                  <strong>Course:</strong> {selectedStudent.course || 'N/A'}
                </div>
                <div>
                  <strong>Study Level:</strong> {selectedStudent.study_level || 'N/A'}
                </div>
                <div>
                  <strong>Year of Registration:</strong> {selectedStudent.year_of_registration || 'N/A'}
                </div>
                <div>
                  <strong>Enrollment Status:</strong> {selectedStudent.enrollment_status || 'N/A'}
                </div>
                <div>
                  <strong>Registration Status:</strong> {selectedStudent.reg_status || 'N/A'}
                </div>
                <div>
                  <strong>Payment Method:</strong> {selectedStudent.payment_method || 'N/A'}
                </div>
              </div>
            </div>

            {/* Academic Records */}
            <div style={{ marginBottom: "25px" }}>
              <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #17a2b8", paddingBottom: "5px" }}>
                Academic Records
              </h3>
              {loadingAcademicRecords ? (
                <div style={{ 
                  padding: "20px", 
                  textAlign: "center", 
                  color: "#6c757d", 
                  backgroundColor: "#f8f9fa", 
                  borderRadius: "4px",
                  border: "1px solid #dee2e6"
                }}>
                  <div className="spinner-border spinner-border-sm" role="status" style={{ marginRight: "10px" }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading academic records...
                </div>
              ) : academicRecords.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <th style={{ border: "1px solid #dee2e6", padding: "8px", textAlign: "left" }}>Institution</th>
                        <th style={{ border: "1px solid #dee2e6", padding: "8px", textAlign: "left" }}>Campus</th>
                        <th style={{ border: "1px solid #dee2e6", padding: "8px", textAlign: "left" }}>Start Date</th>
                        <th style={{ border: "1px solid #dee2e6", padding: "8px", textAlign: "left" }}>End Date</th>
                        <th style={{ border: "1px solid #dee2e6", padding: "8px", textAlign: "left" }}>Year of Study</th>
                        <th style={{ border: "1px solid #dee2e6", padding: "8px", textAlign: "left" }}>Study Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {academicRecords.map((record, index) => (
                        <tr key={index}>
                          <td style={{ border: "1px solid #dee2e6", padding: "8px" }}>
                            {record.institution_name || 'N/A'}
                          </td>
                          <td style={{ border: "1px solid #dee2e6", padding: "8px" }}>
                            {record.campus_name || 'N/A'}
                          </td>
                          <td style={{ border: "1px solid #dee2e6", padding: "8px" }}>
                            {formatDate(record.start_date)}
                          </td>
                          <td style={{ border: "1px solid #dee2e6", padding: "8px" }}>
                            {formatDate(record.end_date)}
                          </td>
                          <td style={{ border: "1px solid #dee2e6", padding: "8px" }}>
                            {record.year_of_study || 'N/A'}
                          </td>
                          <td style={{ border: "1px solid #dee2e6", padding: "8px" }}>
                            {record.study_level || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  padding: "20px", 
                  textAlign: "center", 
                  color: "#6c757d", 
                  backgroundColor: "#f8f9fa", 
                  borderRadius: "4px",
                  border: "1px solid #dee2e6"
                }}>
                  No academic records found for this student.
                </div>
              )}
            </div>

            {/* Status Information */}
            <div style={{ marginBottom: "25px" }}>
              <h3 style={{ color: "#495057", marginBottom: "15px", fontSize: "18px", borderBottom: "2px solid #dc3545", paddingBottom: "5px" }}>
                Status Information
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div>
                  <strong>Document Verified:</strong> 
                  <span style={{ 
                    color: selectedStudent.document_verified ? '#28a745' : '#dc3545',
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}>
                    {selectedStudent.document_verified ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <strong>Policy Agreed:</strong> 
                  <span style={{ 
                    color: selectedStudent.policy_agreed ? '#28a745' : '#dc3545',
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}>
                    {selectedStudent.policy_agreed ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <strong>Wheelchair Required:</strong> 
                  <span style={{ 
                    color: selectedStudent.wheel_chair_req ? '#ffc107' : '#6c757d',
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}>
                    {selectedStudent.wheel_chair_req ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Close button */}
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                onClick={closeStudentDetails}
                style={{
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDocsTable;